<template>
  <div class="anot-shell" :class="{ 'anot-shell--embedded': embedded }">
    <!-- Header -->
    <div class="anot-header">
      <button v-if="!embedded" class="anot-back" type="button" @click="router.back()">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        Voltar
      </button>
      <h1 class="anot-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        Anotações
      </h1>
      <button class="anot-new-btn" type="button" @click="openCreate">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
        Nova anotação
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="anot-loading">Carregando...</div>

    <!-- Empty -->
    <div v-else-if="items.length === 0" class="anot-empty">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
      <p>Nenhuma anotação ainda.</p>
      <button type="button" @click="openCreate">Criar primeira anotação</button>
    </div>

    <!-- List -->
    <div v-else class="anot-list">
      <div
        v-for="ann in items"
        :key="ann.id"
        class="anot-card"
      >
        <div class="anot-card-body" @click="openEdit(ann)">
          <div class="anot-card-title">{{ ann.title || 'Sem título' }}</div>
          <div class="anot-card-content">{{ ann.content }}</div>
          <div class="anot-card-meta">{{ formatDate(ann.updatedAt) }}</div>
        </div>
        <button class="anot-card-delete" type="button" title="Apagar" @click.stop="confirmDelete(ann)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
        </button>
      </div>
    </div>

    <!-- Modal: criar / editar -->
    <Teleport to="body">
      <div v-if="modal" class="anot-overlay" @click.self="closeModal">
        <div class="anot-modal">
          <div class="anot-modal-header">
            <h2>{{ modal.id ? 'Editar anotação' : 'Nova anotação' }}</h2>
            <button type="button" class="anot-modal-close" @click="closeModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="anot-modal-body">
            <input
              v-model="modal.title"
              class="anot-input"
              type="text"
              placeholder="Título (opcional)"
              maxlength="120"
            />
            <textarea
              v-model="modal.content"
              class="anot-textarea"
              placeholder="Escreva sua anotação..."
              rows="8"
              autofocus
            ></textarea>
          </div>
          <div class="anot-modal-footer">
            <button type="button" class="anot-btn-cancel" @click="closeModal">Cancelar</button>
            <button type="button" class="anot-btn-save" :disabled="saving || !modal.content.trim()" @click="saveModal">
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal: confirmar exclusão -->
    <Teleport to="body">
      <div v-if="deleteTarget" class="anot-overlay" @click.self="deleteTarget = null">
        <div class="anot-modal anot-modal--sm">
          <div class="anot-modal-header">
            <h2>Apagar anotação?</h2>
          </div>
          <div class="anot-modal-body">
            <p class="anot-delete-msg">Esta ação não pode ser desfeita.</p>
          </div>
          <div class="anot-modal-footer">
            <button type="button" class="anot-btn-cancel" @click="deleteTarget = null">Cancelar</button>
            <button type="button" class="anot-btn-danger" :disabled="saving" @click="doDelete">
              {{ saving ? 'Apagando...' : 'Apagar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { annotationsStore, type Annotation } from '@/store/annotations'

interface ModalState {
  id: string | null
  title: string
  content: string
}

export default defineComponent({
  name: 'AnotacoesView',
  props: {
    embedded: { type: Boolean, default: false }
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const saving  = ref(false)
    const modal   = ref<ModalState | null>(null)
    const deleteTarget = ref<Annotation | null>(null)

    const items = computed(() => annotationsStore.state.items)

    onMounted(async () => {
      loading.value = true
      try { await annotationsStore.refresh() } finally { loading.value = false }
    })

    const formatDate = (iso: string) => {
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      const today = new Date()
      if (d.toDateString() === today.toDateString()) {
        return `Hoje às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
      }
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }

    const openCreate = () => { modal.value = { id: null, title: '', content: '' } }

    const openEdit = (ann: Annotation) => {
      modal.value = { id: ann.id, title: ann.title, content: ann.content }
    }

    const closeModal = () => { modal.value = null }

    const saveModal = async () => {
      if (!modal.value || !modal.value.content.trim()) return
      saving.value = true
      try {
        if (modal.value.id) {
          await annotationsStore.update(modal.value.id, { title: modal.value.title, content: modal.value.content })
        } else {
          await annotationsStore.create({ title: modal.value.title, content: modal.value.content })
        }
        closeModal()
      } finally { saving.value = false }
    }

    const confirmDelete = (ann: Annotation) => { deleteTarget.value = ann }

    const doDelete = async () => {
      if (!deleteTarget.value) return
      saving.value = true
      try {
        await annotationsStore.remove(deleteTarget.value.id)
        deleteTarget.value = null
      } finally { saving.value = false }
    }

    return {
      router, loading, saving, modal, deleteTarget, items,
      formatDate, openCreate, openEdit, closeModal, saveModal, confirmDelete, doDelete
    }
  }
})
</script>

<style scoped lang="scss">
.anot-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--bg);
  color: var(--ink);
  padding: 0 0 48px;

  &.anot-shell--embedded {
    min-height: unset;
    background: transparent;
  }
}

.anot-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 28px 16px;
  border-bottom: 1px solid oklch(0.5 0 0 / 0.1);
  flex-wrap: wrap;
}

.anot-back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink-muted);
  padding: 0;
  transition: color 0.15s;
  &:hover { color: var(--ink); }
}

.anot-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  flex: 1;
  color: var(--ink);
}

.anot-new-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  border-radius: 10px;
  border: none;
  background: var(--accent);
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
}

.anot-loading {
  padding: 48px 28px;
  color: var(--ink-muted);
  font-size: 14px;
}

.anot-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 72px 28px;
  color: var(--ink-muted);
  text-align: center;

  svg { opacity: 0.3; }

  p { margin: 0; font-size: 15px; }

  button {
    padding: 9px 20px;
    border-radius: 10px;
    border: 1px solid var(--accent);
    background: transparent;
    color: var(--accent);
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    &:hover { background: var(--accent); color: #fff; }
  }
}

.anot-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  padding: 24px 28px;
}

.anot-card {
  display: flex;
  background: var(--card);
  border: 1px solid oklch(0.5 0 0 / 0.1);
  border-radius: 14px;
  overflow: hidden;
  transition: box-shadow 0.18s, transform 0.18s;

  &:hover {
    box-shadow: 0 6px 24px oklch(0 0 0 / 0.1);
    transform: translateY(-2px);

    .anot-card-delete { opacity: 1; }
  }
}

.anot-card-body {
  flex: 1;
  padding: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.anot-card-title {
  font-weight: 700;
  font-size: 14.5px;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.anot-card-content {
  font-size: 13.5px;
  color: var(--ink-muted);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;
}

.anot-card-meta {
  font-size: 11.5px;
  color: oklch(0.5 0 0 / 0.5);
  margin-top: auto;
  padding-top: 8px;
}

.anot-card-delete {
  flex: none;
  width: 38px;
  display: grid;
  place-items: center;
  background: transparent;
  border: none;
  border-left: 1px solid oklch(0.5 0 0 / 0.08);
  cursor: pointer;
  color: oklch(0.55 0.18 25);
  opacity: 0;
  transition: opacity 0.15s, background 0.15s;

  &:hover { background: oklch(0.55 0.18 25 / 0.08); }
}

/* ── OVERLAY / MODAL ── */
.anot-overlay {
  position: fixed;
  inset: 0;
  background: oklch(0 0 0 / 0.4);
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 20px;
}

.anot-modal {
  background: var(--card, #fff);
  border-radius: 18px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 12px 48px oklch(0 0 0 / 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &--sm { max-width: 360px; }
}

.anot-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid oklch(0.5 0 0 / 0.09);

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: var(--ink);
  }
}

.anot-modal-close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--ink-muted);
  padding: 4px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  transition: background 0.15s;
  &:hover { background: oklch(0.5 0 0 / 0.08); }
}

.anot-modal-body {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.anot-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid oklch(0.5 0 0 / 0.15);
  background: var(--bg);
  color: var(--ink);
  font: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.18s;
  box-sizing: border-box;

  &:focus { border-color: var(--accent); }
  &::placeholder { color: oklch(0.5 0 0 / 0.4); }
}

.anot-textarea {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid oklch(0.5 0 0 / 0.15);
  background: var(--bg);
  color: var(--ink);
  font: inherit;
  font-size: 14px;
  line-height: 1.55;
  resize: vertical;
  outline: none;
  transition: border-color 0.18s;
  box-sizing: border-box;

  &:focus { border-color: var(--accent); }
  &::placeholder { color: oklch(0.5 0 0 / 0.4); }
}

.anot-delete-msg {
  margin: 0;
  font-size: 14px;
  color: var(--ink-muted);
}

.anot-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid oklch(0.5 0 0 / 0.09);
}

.anot-btn-cancel {
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid oklch(0.5 0 0 / 0.18);
  background: transparent;
  color: var(--ink-muted);
  font: inherit;
  font-size: 13.5px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: oklch(0.5 0 0 / 0.06); }
}

.anot-btn-save {
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  background: var(--accent);
  color: #fff;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
}

.anot-btn-danger {
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  background: oklch(0.55 0.18 25);
  color: #fff;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover { opacity: 0.88; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
}

@media (max-width: 600px) {
  .anot-header { padding: 14px 16px 12px; }
  .anot-list { padding: 16px; gap: 12px; grid-template-columns: 1fr; }
}
</style>
